import {
    CheckOutlined,
    Notifications,
    NotificationsNone,
} from '@mui/icons-material'
import {
    Badge,
    IconButton,
    ListItemIcon,
    Menu,
    Typography,
} from '@mui/material'
import { useMatchRoute, useMatches } from '@tanstack/react-router'
import { formatISO } from 'date-fns'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNotifications } from '../queries/useNotifications'
import { MenuItemLink } from '@/components/MenuItemLink'
import { dateTimeOffsetDtoToDate } from '@/helper/dateTimeOffsetDto'
import { stringifyId } from '@/helper/id'
import { useCalendarForTrackingsOverviewKey } from '@/helper/userSettingsKeys'
import { useBooleanUserSetting } from '@/queries/useUserSettings'

/**
 * Displayes an `IconButton` that expands into a `Menu` that displayes notifications sent from the backend.
 * The `IconButton` also has a `Badge` that displayes the notification count.
 */
export const NotificationsMenu: React.FC = () => {
    const [notificationsMenuEl, setNotificationsMenuEl] =
        React.useState<null | HTMLElement>(null)

    const handleClose = () => setNotificationsMenuEl(null)

    const { t } = useTranslation()

    const {
        res: { data: { trackings = [] } = {} },
        markAsSeen,
    } = useNotifications()

    const matchRoute = useMatchRoute()

    const indexRoute = matchRoute({ to: '/' })

    const matches = useMatches()
    const { fullPath: activePath = '/' } = matches.at(-1) ?? {}

    const { data: useCalendarForTrackings } = useBooleanUserSetting(
        useCalendarForTrackingsOverviewKey
    )

    return (
        <>
            <IconButton
                onClick={({ currentTarget }) =>
                    setNotificationsMenuEl(currentTarget)
                }
            >
                {trackings.length > 0 ?
                    <Badge badgeContent={trackings.length} max={9}>
                        <Notifications />
                    </Badge>
                :   <NotificationsNone />}
            </IconButton>

            <Menu
                anchorEl={notificationsMenuEl}
                open={!!notificationsMenuEl}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                }}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: {
                                xl: '25%',
                                md: '50%',
                                xs: '100%',
                            },
                            maxHeight: 250,
                        },
                    },
                }}
            >
                {trackings.length > 0 ?
                    trackings.map(tr => {
                        const id = stringifyId(tr.id)
                        return (
                            <MenuItemLink
                                key={id}
                                from={activePath}
                                to={
                                    indexRoute ? '/'
                                    : useCalendarForTrackings ?
                                        '/trackings/calendar'
                                    :   '/trackings/list'
                                }
                                search={{
                                    showEditTrackings: true,
                                    day:
                                        tr.startedAt ?
                                            formatISO(
                                                dateTimeOffsetDtoToDate(
                                                    tr.startedAt
                                                ),
                                                { representation: 'date' }
                                            )
                                        :   '',
                                    trackingId: id,
                                }}
                            >
                                <ListItemIcon>
                                    <IconButton
                                        onClick={e => {
                                            e.preventDefault()

                                            markAsSeen(id)
                                        }}
                                        size={'small'}
                                    >
                                        <CheckOutlined />
                                    </IconButton>
                                </ListItemIcon>

                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                    }}
                                >
                                    <Typography variant={'body1'}>
                                        {t('notifications.trackingAborted')}
                                    </Typography>
                                    {tr.startedAt && tr.endedAt && (
                                        <Typography
                                            variant={'body2'}
                                            color={'textDisabled'}
                                        >
                                            {t(
                                                'notifications.trackingStartEnd',
                                                {
                                                    start: dateTimeOffsetDtoToDate(
                                                        tr.startedAt
                                                    ),
                                                    end: dateTimeOffsetDtoToDate(
                                                        tr.endedAt
                                                    ),
                                                }
                                            )}
                                        </Typography>
                                    )}
                                </div>
                            </MenuItemLink>
                        )
                    })
                :   <Typography
                        variant={'h6'}
                        color={'textDisabled'}
                        style={{ textAlign: 'center' }}
                    >
                        {t('notifications.noNotifications')}
                    </Typography>
                }
            </Menu>
        </>
    )
}
